import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { googleSignIn, googleSignUp } from '../DataService';
import { saveClaims } from '../CookieHelper';
import { theme } from '../styles/theme/theme';
import { AuthPageContainer, AuthFormCard, AuthPageTitle, AuthErrorMessage, AuthSuccessMessage } from '../styles/components';
import { CircularProgress, ThemeProvider } from '@mui/material'; 

const GoogleCallback: React.FC = () => {
  const location = useLocation();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const authStarted = useRef(false);

  const handleAuth = useCallback(async () => {
    const params = new URLSearchParams(location.search);
    const idToken = params.get('id_token');
    const flow = params.get('flow'); // 'signin' or 'signup'

    if (!idToken) {
      setError('Google authentication failed. No token provided.');
      return;
    }

    // Prevent multiple auth attempts
    if (authStarted.current) {
      return;
    }

    authStarted.current = true;
    
    try {
      const decodedToken: { name: string, email: string } = jwtDecode(idToken);

      let res;
      if (flow === 'signup') {
        res = await googleSignUp({ name: decodedToken.name, email: decodedToken.email, token: idToken });
      } else {
        res = await googleSignIn({ email: decodedToken.email, token: idToken });
      }

      saveClaims(res.Claims);
      setSuccess('Authentication successful! Redirecting...');

      // Redirect logic from SignIn.tsx
      let accountUrl = '';
      const urlParam = localStorage.getItem('url');
      if (urlParam) {
        accountUrl = decodeURIComponent(urlParam);
      } else {
        accountUrl = res.RedirectUrl;
      }

      // Append all claims as query parameters
      const claimsToAppend = [
        { param: 'uid', value: res.Claims.UserId },
        { param: 'aid', value: res.Claims.AccountId },
        { param: 'email', value: res.Claims.Email },
        { param: 'name', value: res.Claims.Name },
      ];

      const url = new URL(accountUrl, window.location.origin);
      claimsToAppend.forEach(({ param, value }) => {
        if (value && !url.searchParams.has(param)) {
          url.searchParams.append(param, value);
        }
      });
      
      accountUrl = url.toString();
      localStorage.removeItem('url');
      
      setTimeout(() => {
        window.location.href = accountUrl;
      }, 2000);

    } catch (err: any) {
      setError(err.message || 'An error occurred during Google authentication.');
    }
  }, [location.search]);

  useEffect(() => {
    handleAuth();
  }, [handleAuth]);

  return (
    <ThemeProvider theme={theme}>
      <AuthPageContainer>
        <AuthFormCard>
          <AuthPageTitle>Authenticating with Google</AuthPageTitle>
          {error && <AuthErrorMessage>{error}</AuthErrorMessage>}
          {success && <AuthSuccessMessage>{success}</AuthSuccessMessage>}
          {!error && !success && <CircularProgress sx={{ mt: 4 }} />}
        </AuthFormCard>
      </AuthPageContainer>
    </ThemeProvider>
  );
};

export default GoogleCallback; 